// ./src/phoneHelpers.ts
//
// Unified phone-number normalization for the FE — the mirror of the API's
// `src/utils/phone.ts`. Thin wrapper over `libphonenumber-js`; same library,
// same behavior. Store/emit E.164 (`+15877142557`) when valid, keep the raw
// input otherwise (flag, never drop). Isomorphic / import-safe (no DOM/React).

import {
  parsePhoneNumberFromString,
  isPossiblePhoneNumber,
  AsYouType,
  getCountryCallingCode,
  type CountryCode,
} from "libphonenumber-js";

/** ISO 3166-1 alpha-2 region code (re-exported so consumers needn't depend on libphonenumber-js directly). */
export type PhoneCountryCode = CountryCode;

/** Default parse region when the field has no country hint. */
export const DEFAULT_PHONE_REGION: CountryCode = "US";

/**
 * The regions the app targets, in the order they appear in the country dropdown.
 * US + territories (PR/GU/VI/AS/MP), Canada, UK, Australia, New Zealand. Flags are
 * emoji so no asset pipeline is needed. `callingCode` is derived, not hard-coded.
 */
export interface PhoneCountry {
  code: CountryCode;
  name: string;
  flag: string;
  callingCode: string;
}

const COUNTRY_DEFS: Array<Omit<PhoneCountry, "callingCode">> = [
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "NZ", name: "New Zealand", flag: "🇳🇿" },
  { code: "PR", name: "Puerto Rico", flag: "🇵🇷" },
  { code: "GU", name: "Guam", flag: "🇬🇺" },
  { code: "VI", name: "U.S. Virgin Islands", flag: "🇻🇮" },
  { code: "AS", name: "American Samoa", flag: "🇦🇸" },
  { code: "MP", name: "Northern Mariana Islands", flag: "🇲🇵" },
];

export const PHONE_COUNTRIES: PhoneCountry[] = COUNTRY_DEFS.map((c) => ({
  ...c,
  callingCode: `+${getCountryCallingCode(c.code)}`,
}));

export interface NormalizedPhone {
  /** The value to STORE/EMIT — E.164 when valid, else the raw trimmed input, else null. */
  value: string | null;
  /** Canonical E.164 (`+…`) when valid, else null. */
  e164: string | null;
  /** Whether the input is a valid phone number for its (detected/default) region. */
  isValid: boolean;
  /** ISO 3166-1 alpha-2 region libphonenumber resolved, else null. */
  region: CountryCode | null;
  /** Pretty international form for display, else the raw input. */
  formatted: string | null;
}

const EMPTY: NormalizedPhone = { value: null, e164: null, isValid: false, region: null, formatted: null };

/**
 * Normalize loose phone input into a canonical shape. Invalid input is kept raw
 * (flagged), never discarded. Mirrors the API helper of the same name.
 *
 * @param input - Raw phone input (any format) or null/undefined.
 * @param defaultRegion - Parse region for bare local numbers (default 'US').
 */
export const normalizePhone = (input: unknown, defaultRegion: CountryCode = DEFAULT_PHONE_REGION): NormalizedPhone => {
  if (input == null) {
    return EMPTY;
  }

  const raw = String(input).trim();

  if (!raw) {
    return EMPTY;
  }

  const parsed = parsePhoneNumberFromString(raw, defaultRegion);

  if (parsed && parsed.isPossible()) {
    return {
      value: parsed.number,
      e164: parsed.number,
      isValid: true,
      region: (parsed.country as CountryCode) ?? null,
      formatted: parsed.formatInternational(),
    };
  }

  return { value: raw, e164: null, isValid: false, region: null, formatted: raw };
};

/**
 * Canonical E.164 for a valid number, else null.
 *
 * @param input - Raw phone input or null/undefined.
 * @param defaultRegion - Parse region for bare local numbers (default 'US').
 */
export const toE164 = (input: unknown, defaultRegion: CountryCode = DEFAULT_PHONE_REGION): string | null => {
  return normalizePhone(input, defaultRegion).e164;
};

/**
 * Whether the input is a usable, sendable phone number (right length/shape —
 * `isPossible`, not the over-strict `isValid`, which rejects fictional and many
 * real numbers alike). Mirrors the API helper.
 *
 * @param input - Raw phone input or null/undefined.
 * @param defaultRegion - Parse region for bare local numbers (default 'US').
 */
export const isValidPhone = (input: unknown, defaultRegion: CountryCode = DEFAULT_PHONE_REGION): boolean => {
  if (input == null) {
    return false;
  }

  const raw = String(input).trim();

  if (!raw) {
    return false;
  }

  return isPossiblePhoneNumber(raw, defaultRegion);
};

/**
 * Pretty international form for display (`+1 587 714 2557`). Falls back to the
 * raw input when it can't be parsed, so a flagged/invalid number still shows.
 *
 * @param input - Stored phone value (usually E.164) or raw input.
 * @param defaultRegion - Parse region for bare local numbers (default 'US').
 */
export const formatPhone = (input: unknown, defaultRegion: CountryCode = DEFAULT_PHONE_REGION): string => {
  if (input == null) {
    return "";
  }

  const raw = String(input).trim();

  if (!raw) {
    return "";
  }

  const parsed = parsePhoneNumberFromString(raw, defaultRegion);

  return parsed ? parsed.formatInternational() : raw;
};

/**
 * Live "as you type" formatting for an input field, scoped to a country. Returns
 * the nicely-spaced national/international text for the digits entered so far.
 * Emit E.164 on change via `toE164`; use this only for the visible value.
 *
 * @param input - The current raw text in the field.
 * @param region - The selected country (drives spacing/grouping).
 */
export const formatPhoneAsYouType = (input: string, region: CountryCode = DEFAULT_PHONE_REGION): string => {
  if (!input) {
    return "";
  }

  return new AsYouType(region).input(input);
};

/**
 * Best-effort region detection from a stored value (used to preselect the
 * country dropdown when editing an existing E.164 number). Falls back to the
 * given default.
 *
 * @param input - Stored phone value (usually E.164).
 * @param fallback - Region to return when detection fails (default 'US').
 */
export const detectPhoneRegion = (input: unknown, fallback: CountryCode = DEFAULT_PHONE_REGION): CountryCode => {
  if (input == null) {
    return fallback;
  }

  const raw = String(input).trim();

  if (!raw) {
    return fallback;
  }

  const parsed = parsePhoneNumberFromString(raw);

  return (parsed?.country as CountryCode) ?? fallback;
};
